// Raíz de la app: providers (auth + API) y enrutado.
//
// Dos capas de rutas conviven bajo un único router:
//  - CAPA PÚBLICA (marketing / corporativa): home, funciones, precios, registro,
//    contacto y login. Accesible SIN sesión y optimizada para SEO. Usa un layout
//    ancho de escritorio (MarketingLayout).
//  - CAPA PRIVADA (app interna): el catálogo y todas las secciones gobernadas
//    por permisos (Req. 19.10). Requiere sesión; si no hay, se redirige al home.
//
// La ruta "/" es ambivalente: con sesión muestra el catálogo (app interna); sin
// sesión muestra el home de marketing. El resto de rutas de marketing son
// públicas siempre; las rutas internas exigen sesión.

import { useCallback } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './auth/AuthContext.js';
import { usePageTracking } from './analytics/usePageTracking.js';
import { LegalGate } from './auth/LegalGate.js';
import { CookieBanner } from './consent/CookieBanner.js';
import { ApiProvider } from './api/ApiContext.js';
import { LanguageProvider } from './i18n/LanguageProvider.js';
import { OnboardingProvider } from './onboarding/OnboardingContext.js';
import { ApiClient } from './api/client.js';
import { createApi } from './api/endpoints.js';
import { config } from './config.js';
import { Layout } from './Layout.js';
import { LoginView } from './views/LoginView.js';
import { AuthCallbackView } from './views/AuthCallbackView.js';
import { Spinner } from './ui/components.js';
import { ToastProvider } from './ui/ToastProvider.js';
import { useFontsReady } from './ui/useFontsReady.js';
import type { Feature, MePermissions, PermissionLevel } from './types.js';

// Capa pública (marketing / corporativa).
import { MarketingLayout } from './marketing/MarketingLayout.js';
import { HomeView } from './marketing/pages/HomeView.js';
import { FeaturesView } from './marketing/pages/FeaturesView.js';
import { PricingView } from './marketing/pages/PricingView.js';
import { RegisterView } from './marketing/pages/RegisterView.js';
import { ContactView } from './marketing/pages/ContactView.js';
import { LegalView } from './marketing/pages/LegalView.js';

// Vistas.
import { CatalogView } from './views/CatalogView.js';
import { RecordDetailView } from './views/RecordDetailView.js';
import { EditVinylView } from './views/EditVinylView.js';
import { AddVinylView } from './views/AddVinylView.js';
import { WishlistView } from './views/WishlistView.js';
import { DuplicatesView } from './views/DuplicatesView.js';
import { TopView } from './views/TopView.js';
import { LocationsView } from './views/LocationsView.js';
import { GenreTaxonomyView } from './views/GenreTaxonomyView.js';
import { HistoryView } from './views/HistoryView.js';
import { DealHunterSettingsView } from './views/DealHunterSettingsView.js';
import { BulkSyncView } from './views/BulkSyncView.js';
import { QuickLookupView } from './views/QuickLookupView.js';
import { AudioView } from './views/AudioView.js';
import { StoreLocatorView } from './views/StoreLocatorView.js';
import { AdminView } from './views/AdminView.js';
import { DiscogsFormatsView } from './views/config/DiscogsFormatsView.js';
import { RecordFieldsView } from './views/config/RecordFieldsView.js';
import { SyncRulesView } from './views/config/SyncRulesView.js';

import { CurationRulesView } from './views/config/CurationRulesView.js';
import { SupportView } from './views/SupportView.js';
import { UserGuideView } from './views/UserGuideView.js';
import { AccountView } from './views/AccountView.js';
import { SubscriptionView } from './views/SubscriptionView.js';
import { ChangePasswordView } from './views/ChangePasswordView.js';
import { DataManagementView } from './views/DataManagementView.js';
import { AnalyticsView } from './views/AnalyticsView.js';
import { MenuView } from './views/MenuView.js';
import { AccountsAdminView } from './views/AccountsAdminView.js';
import { PlansAdminView } from './views/PlansAdminView.js';
import { LegalAdminView } from './views/LegalAdminView.js';
import { GlobalAnalyticsView } from './views/GlobalAnalyticsView.js';
import { GlobalDealHunterView } from './views/GlobalDealHunterView.js';
import { GlobalLocalesView } from './views/GlobalLocalesView.js';
import { GlobalLogsView } from './views/GlobalLogsView.js';
import { AuditLogsView } from './views/AuditLogsView.js';
import { ServiceCasesView } from './views/ServiceCasesView.js';

/** Carga los permisos con un cliente API efímero (solo necesita el token). */
function fetchPermissions(idToken: string): Promise<MePermissions> {
  const client = new ApiClient(config.apiUrl, () => idToken);
  return createApi(client).me.permissions();
}

/**
 * Deja rastro de un inicio de sesión exitoso en el log de actividad. `method`
 * identifica el proveedor del login (`password` por defecto para email+contraseña;
 * `google`/`apple`/`federated` para el flujo federado). Best-effort: cualquier
 * fallo se ignora en silencio (no debe afectar la entrada del usuario).
 */
function recordLoginEvent(idToken: string, method = 'password'): Promise<void> {
  const client = new ApiClient(config.apiUrl, () => idToken);
  return createApi(client)
    .me.loginEvent(method)
    .then(() => undefined)
    .catch(() => undefined);
}

/** Ruta protegida por una funcionalidad/nivel; si no hay permiso, muestra aviso. */
function Guard({
  feature,
  level = 'read',
  ownerOnly,
  systemAdminOnly,
  children,
}: {
  feature: Feature | null;
  level?: PermissionLevel;
  ownerOnly?: boolean;
  /** Exclusivo del administrador global de la plataforma (system_admin). */
  systemAdminOnly?: boolean;
  children: React.ReactNode;
}) {
  const { hasPermission, isOwner, isSystemAdmin } = useAuth();
  if (systemAdminOnly && !isSystemAdmin()) return <NoAccess />;
  if (ownerOnly && !isOwner()) return <NoAccess />;
  if (feature && !hasPermission(feature, level)) return <NoAccess />;
  return <>{children}</>;
}

function NoAccess() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-space-md px-margin-mobile py-space-xl text-center">
      <span className="w-20 h-20 rounded-full bg-priority-urgent/15 border border-priority-urgent/40 flex items-center justify-center">
        <span className="material-symbols-outlined text-priority-urgent text-[40px]">lock</span>
      </span>
      <div className="flex flex-col gap-space-xs">
        <span className="font-badge-micro text-badge-micro text-priority-urgent uppercase tracking-widest">
          {t('access.errorCode', { code: 403 })}
        </span>
        <h1 className="font-headline-lg-mobile text-headline-lg-mobile font-bold text-on-surface">
          {t('access.forbiddenTitle')}
        </h1>
        <p className="font-body-sm text-body-sm text-on-surface-variant max-w-xs">
          {t('access.forbiddenBody')}
        </p>
      </div>
      <a href="/" className="btn-secondary">
        <span className="material-symbols-outlined text-[18px]">arrow_back</span>{' '}
        {t('access.backToCatalog')}
      </a>
    </div>
  );
}

/** Contenido tras autenticar: rutas + layout. */
function AuthenticatedApp() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route
          index
          element={
            <Guard feature="catalog">
              <CatalogView />
            </Guard>
          }
        />
        <Route
          path="records/:id"
          element={
            <Guard feature="catalog">
              <RecordDetailView />
            </Guard>
          }
        />
        <Route
          path="records/:id/edit"
          element={
            <Guard feature="catalog" level="read_write">
              <EditVinylView />
            </Guard>
          }
        />
        <Route
          path="add"
          element={
            <Guard feature="ingestion">
              <AddVinylView />
            </Guard>
          }
        />
        <Route
          path="quick-lookup"
          element={
            <Guard feature="quick_lookup">
              <QuickLookupView />
            </Guard>
          }
        />
        <Route
          path="audio"
          element={
            <Guard feature="quick_lookup">
              <AudioView />
            </Guard>
          }
        />
        {/* Localizador de Tiendas Físicas: herramienta de catálogo (misma feature
            que la consulta rápida / audio). */}
        <Route
          path="store-locator"
          element={
            <Guard feature="quick_lookup">
              <StoreLocatorView />
            </Guard>
          }
        />
        <Route
          path="wishlist"
          element={
            <Guard feature="wishlist">
              <WishlistView />
            </Guard>
          }
        />
        <Route
          path="duplicates"
          element={
            <Guard feature="duplicates">
              <DuplicatesView />
            </Guard>
          }
        />
        <Route
          path="top"
          element={
            <Guard feature="top">
              <TopView />
            </Guard>
          }
        />
        <Route
          path="locations"
          element={
            <Guard feature="locations">
              <LocationsView />
            </Guard>
          }
        />
        <Route
          path="taxonomy"
          element={
            <Guard feature="catalog">
              <GenreTaxonomyView />
            </Guard>
          }
        />
        <Route
          path="sync"
          element={
            <Guard feature="sync">
              <BulkSyncView />
            </Guard>
          }
        />

        <Route
          path="analytics"
          element={
            <Guard feature="catalog">
              <AnalyticsView />
            </Guard>
          }
        />
        <Route
          path="history"
          element={
            <Guard feature="ingestion">
              <HistoryView />
            </Guard>
          }
        />
        {/* Logs & Auditoría (Herramientas de Catálogo): solo el propietario ve
            el registro de actividad de su propio catálogo. */}
        <Route
          path="audit"
          element={
            <Guard feature="catalog" ownerOnly>
              <AuditLogsView />
            </Guard>
          }
        />
        <Route
          path="admin"
          element={
            <Guard feature="user_admin" ownerOnly>
              <AdminView />
            </Guard>
          }
        />
        <Route
          path="config/formats"
          element={
            <Guard feature="user_admin" ownerOnly>
              <DiscogsFormatsView />
            </Guard>
          }
        />
        <Route
          path="config/fields"
          element={
            <Guard feature="user_admin" ownerOnly>
              <RecordFieldsView />
            </Guard>
          }
        />
        <Route
          path="config/sync-rules"
          element={
            <Guard feature="user_admin" ownerOnly>
              <SyncRulesView />
            </Guard>
          }
        />

        <Route
          path="config/curation-rules"
          element={
            <Guard feature="user_admin" ownerOnly>
              <CurationRulesView />
            </Guard>
          }
        />
        <Route
          path="config/deal-hunter"
          element={
            <Guard feature="deal_hunter" ownerOnly>
              <DealHunterSettingsView />
            </Guard>
          }
        />
        {/* Mesa de ayuda / soporte: disponible para cualquier usuario autenticado. */}
        <Route
          path="support"
          element={
            <Guard feature={null}>
              <SupportView />
            </Guard>
          }
        />
        {/* Guía de Usuario (materiales de apoyo en PDF): cualquier usuario autenticado. */}
        <Route
          path="guides"
          element={
            <Guard feature={null}>
              <UserGuideView />
            </Guard>
          }
        />
        {/* Mi cuenta (avatar): cualquier usuario autenticado. */}
        <Route
          path="account"
          element={
            <Guard feature={null}>
              <AccountView />
            </Guard>
          }
        />
        {/* Suscripción (Stripe Billing): solo el propietario de la cuenta. */}
        <Route
          path="billing"
          element={
            <Guard feature={null} ownerOnly>
              <SubscriptionView />
            </Guard>
          }
        />
        {/* Cambio de contraseña (Ayuda & Cuenta): cualquier usuario autenticado. */}
        <Route
          path="account/password"
          element={
            <Guard feature={null}>
              <ChangePasswordView />
            </Guard>
          }
        />
        <Route
          path="data"
          element={
            <Guard feature="catalog" ownerOnly>
              <DataManagementView />
            </Guard>
          }
        />
        {/* Administración de la plataforma: EXCLUSIVO del administrador global
            (system_admin). Los owners de tenant NO la ven. Placeholders por definir. */}
        <Route
          path="admin/accounts"
          element={
            <Guard feature={null} systemAdminOnly>
              <AccountsAdminView />
            </Guard>
          }
        />
        <Route
          path="admin/service-cases"
          element={
            <Guard feature={null} systemAdminOnly>
              <ServiceCasesView />
            </Guard>
          }
        />
        <Route
          path="admin/plans"
          element={
            <Guard feature={null} systemAdminOnly>
              <PlansAdminView />
            </Guard>
          }
        />
        <Route
          path="admin/legal"
          element={
            <Guard feature={null} systemAdminOnly>
              <LegalAdminView />
            </Guard>
          }
        />
        <Route
          path="admin/analytics"
          element={
            <Guard feature={null} systemAdminOnly>
              <GlobalAnalyticsView />
            </Guard>
          }
        />
        <Route
          path="admin/deal-hunter"
          element={
            <Guard feature={null} systemAdminOnly>
              <GlobalDealHunterView />
            </Guard>
          }
        />
        <Route
          path="admin/locales"
          element={
            <Guard feature={null} systemAdminOnly>
              <GlobalLocalesView />
            </Guard>
          }
        />
        <Route
          path="admin/logs"
          element={
            <Guard feature={null} systemAdminOnly>
              <GlobalLogsView />
            </Guard>
          }
        />
        <Route path="menu" element={<MenuView />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/** Envuelve la app interna con la puerta legal (la API ya está provista arriba). */
function PrivateApp() {
  const { t } = useTranslation();
  const auth = useAuth();

  if (auth.loading) return <Spinner label={t('common.loadingSession')} />;

  return (
    <LegalGate>
      <AuthenticatedApp />
    </LegalGate>
  );
}

/**
 * Puerta de la app interna: exige sesión. Sin sesión, redirige al login
 * conservando la intención (podría ampliarse a `?next=`). La ruta "/" la maneja
 * `RootRoute` para no colisionar con el home de marketing.
 */
function RequireAuth() {
  const { authenticated } = useAuth();
  const location = useLocation();
  // Sin sesión: preservamos la INTENCIÓN (deep-link a un disco, a billing…)
  // para no perderla tras autenticar. Enviamos al login con `?next=<destino>`
  // (ruta interna + query actuales). `LoginRoute` ya sanea `next` a rutas
  // internas relativas. La raíz "/" la maneja `RootRoute`/`PublicHomeRoute`, así
  // que aquí solo llegamos a rutas internas: si el destino es "/", caemos al
  // login sin `next` (el login, si ya hay sesión, vuelve a "/").
  if (!authenticated) {
    const dest = `${location.pathname}${location.search}`;
    const next = dest && dest !== '/' ? `?next=${encodeURIComponent(dest)}` : '';
    return <Navigate to={`/login${next}`} replace />;
  }
  return <PrivateApp />;
}

/**
 * Login como PÁGINA pública: si ya hay sesión, va a la app. Honra `?next=` para
 * continuar un flujo iniciado antes de autenticar (p. ej. tras el auto-registro
 * de un plan de pago: `/login?next=/billing?checkout=auto...`). Solo se aceptan
 * destinos internos relativos (empiezan con "/") para no permitir redirecciones
 * abiertas a otros orígenes.
 */
function LoginRoute() {
  const { authenticated } = useAuth();
  const [params] = useSearchParams();
  if (authenticated) {
    const next = params.get('next');
    const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
    return <Navigate to={safeNext} replace />;
  }
  return <LoginView />;
}

/**
 * Home público de marketing: SOLO para visitantes sin sesión. Si el usuario YA
 * está autenticado, "/" NO debe mostrar el home corporativo (header + footer de
 * marketing), sino el catálogo dentro del shell interno; para eso delegamos en
 * la capa privada (`RequireAuth` → `AuthenticatedApp`, cuyo índice es el
 * catálogo). Así "/" autenticado nunca hereda el `MarketingLayout`.
 */
function PublicHomeRoute() {
  const { authenticated } = useAuth();
  if (authenticated) return <RequireAuth />;
  return (
    <MarketingLayout>
      <HomeView />
    </MarketingLayout>
  );
}

/** Enrutado raíz: capa pública (marketing) + capa privada (app interna). */
function AppRoutes() {
  const { t } = useTranslation();
  const fontsReady = useFontsReady();
  // Rastreo de page views en cada cambio de ruta (GA4/Hotjar). Debe ir dentro
  // del <BrowserRouter>; es no-op si no hay IDs de analítica configurados.
  usePageTracking();

  // Hasta que las fuentes (iconos Material Symbols) estén listas, spinner global.
  if (!fontsReady) return <Spinner label={t('common.loading')} />;

  return (
    <Routes>
      {/* --- Capa pública (marketing / corporativa) con layout ancho ---
          OJO: "/" NO se incluye aquí para que, con sesión, el catálogo use el
          shell interno (sin header/footer de marketing). Las demás rutas de
          marketing son siempre públicas. */}
      <Route element={<MarketingLayout />}>
        <Route path="/funciones" element={<FeaturesView />} />
        <Route path="/precios" element={<PricingView />} />
        <Route path="/registro" element={<RegisterView />} />
        <Route path="/contacto" element={<ContactView />} />
        <Route path="/legal/:doc" element={<LegalView />} />
      </Route>

      {/* --- "/" ambivalente: sin sesión → home de marketing (con su layout
             propio); con sesión → catálogo en el shell interno. --- */}
      <Route path="/" element={<PublicHomeRoute />} />

      {/* --- Login (página pública, sin header de marketing) --- */}
      <Route path="/login" element={<LoginRoute />} />

      {/* --- Callback del login federado (Google/Apple). Pública: procesa el
             `code` de Cognito, guarda la sesión y redirige a la app. --- */}
      <Route path="/auth/callback" element={<AuthCallbackView />} />

      {/* --- Capa privada (app interna) protegida por sesión --- */}
      <Route path="/*" element={<RequireAuth />} />
    </Routes>
  );
}

/**
 * Puente de proveedores que dependen de la sesión: monta la API (con el token
 * vigente) y el proveedor de idioma POR ENCIMA de ambas capas (pública y
 * privada), para que el selector de idioma funcione con y sin sesión y la
 * preferencia del usuario (del backend) prevalezca al autenticar.
 */
function SessionProviders() {
  const auth = useAuth();
  const getToken = useCallback(() => auth.idToken, [auth.idToken]);
  const onUnauthorized = useCallback(() => {
    void auth.signOut();
  }, [auth]);

  return (
    <ApiProvider getToken={getToken} onUnauthorized={onUnauthorized}>
      <LanguageProvider>
        {/* Toasts globales: por encima de ambas capas (pública y privada) para
            que cualquier vista emita confirmaciones/errores efímeros uniformes. */}
        <ToastProvider>
          {/* Onboarding guiado: envuelve ambas capas (dentro de API + router) para
              poder navegar, leer permisos y persistir el estado del tour. El
              overlay solo se pinta cuando hay un recorrido activo (app interna). */}
          <OnboardingProvider>
            <AppRoutes />
          </OnboardingProvider>
        </ToastProvider>
      </LanguageProvider>
    </ApiProvider>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider fetchPermissions={fetchPermissions} recordLoginEvent={recordLoginEvent}>
        <SessionProviders />
        {/* Banner de consentimiento de cookies: global (dentro del router para
            usar <Link>), por encima de ambas capas. Gobierna la carga de la
            analítica de terceros (consentimiento previo, ePrivacy/GDPR). */}
        <CookieBanner />
      </AuthProvider>
    </BrowserRouter>
  );
}
